'use client';

import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  MessageSquare,
  Search,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/lib/api/trpc';
import { formatMoney } from '@/lib/money';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { useAuditPackDownload } from '@/hooks/use-audit-pack-download';
import { PARTNER_ADMIN_ROUTES } from '@/config/router';
import type { AppRouter } from '@tagadapay/trpc-types';
import type { inferRouterOutputs } from '@trpc/server';

type Statement = inferRouterOutputs<AppRouter>['partnerReports']['get'];
/** Follow-up fields are attached at read time by `partnerReports.get`. */
type AccountLine = NonNullable<Statement['byAccount']>[number] & {
  settledLater?: {
    partnerReportId: string;
    periodStart: Date | string;
    periodEnd: Date | string;
    markupCents: number;
  };
  invoiceNowPaid?: boolean;
};
type Adjustment = NonNullable<Statement['adjustments']>[number];

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const isResolvedAfterFreeze = (a: AccountLine) => !!a.settledLater || !!a.invoiceNowPaid;

/** Link / note explaining what happened to a freeze-unpaid invoice after the snapshot. */
function CollectionFollowUpNote({ line, partner }: { line: AccountLine; partner: string }) {
  if (line.settledLater) {
    const s = line.settledLater;
    return (
      <Link
        to={PARTNER_ADMIN_ROUTES(partner).REPORT_DETAILS(s.partnerReportId)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400"
        title="Open the statement that credited this late payment"
      >
        Credited on {fmtDate(s.periodStart)} → {fmtDate(s.periodEnd)}
      </Link>
    );
  }
  if (line.invoiceNowPaid) {
    return (
      <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
        Paid — credited on next statement
      </span>
    );
  }
  return null;
}

const STATUS_STYLE: Record<string, string> = {
  finalized: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  sent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  invoiced: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  paid: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  draft: 'bg-muted text-muted-foreground',
  voided: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
};

/** Download the statement's audit pack (PDF + CSVs) as a ZIP blob. */
function AuditPackButton({ partner, reportId }: { partner: string; reportId: string }) {
  const { downloadOne, pendingId } = useAuditPackDownload(partner);
  const pending = pendingId === reportId;

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={pending}
      onClick={() => downloadOne(reportId)}
      title="Summary PDF + justification CSVs (fee lines, sell rates, buy-rate grid, adjustments)"
    >
      {pending ? (
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
      ) : (
        <Download className="mr-1.5 h-3.5 w-3.5" />
      )}
      Audit pack
    </Button>
  );
}

/** Header: period, status, model, delivery + partner invoice link. */
function StatementHeader({ partner, data }: { partner: string; data: Statement }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to={PARTNER_ADMIN_ROUTES(partner).REPORTS}
          className="text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All statements
        </Link>
        <h2 className="text-xl font-semibold">
          Statement {fmtDate(data.periodStart)} → {fmtDate(data.periodEnd)}
        </h2>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
              STATUS_STYLE[data.status] ?? 'bg-muted text-muted-foreground'
            }`}
          >
            {data.status}
          </span>
          <Badge variant="secondary" className="text-xs">
            {data.model === 'buy_rate' ? 'Buy rate' : 'Rev share'}
          </Badge>
          <span className="text-muted-foreground inline-flex items-center gap-2 text-xs">
            <Mail
              className={`h-3.5 w-3.5 ${data.emailSentAt ? 'text-emerald-600' : 'opacity-30'}`}
            />
            <MessageSquare
              className={`h-3.5 w-3.5 ${data.slackSentAt ? 'text-emerald-600' : 'opacity-30'}`}
            />
          </span>
        </div>
      </div>
      <div className="text-right">
        <p className="text-muted-foreground text-xs">Total owed to you</p>
        <p className="text-3xl font-bold tabular-nums text-emerald-600">
          {formatMoney(data.totalOwedCents, 'USD')}
        </p>
        <div className="mt-2 flex justify-end">
          <AuditPackButton partner={partner} reportId={data.id} />
        </div>
        {data.partnerInvoiceUrl && (
          <a
            href={data.partnerInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-foreground mt-1 inline-flex items-center gap-1 text-xs"
          >
            {data.partnerInvoiceNumber ?? 'Invoice'}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

/** How the total is built: markup + reconciliation lines = total owed. */
function OwedBreakdown({ data }: { data: Statement }) {
  const rows: { label: string; cents: number; signed?: boolean }[] = [
    { label: 'Markup on collected invoices', cents: data.cohortMarkupPaidCents },
  ];
  if (data.latePaymentMarkupCents > 0)
    rows.push({ label: 'Late payments settled', cents: data.latePaymentMarkupCents, signed: true });
  if (data.refundClawbackCents > 0)
    rows.push({
      label: 'Refunds / chargebacks (clawback)',
      cents: -data.refundClawbackCents,
      signed: true,
    });
  if (data.processingShareCents > 0)
    rows.push({ label: 'Processing share', cents: data.processingShareCents, signed: true });

  return (
    <div className="rounded-lg border">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between border-b px-4 py-2.5 text-sm">
          <span className="text-muted-foreground">{r.label}</span>
          <span
            className={`font-medium tabular-nums ${
              !r.signed ? '' : r.cents < 0 ? 'text-red-600' : 'text-emerald-600'
            }`}
          >
            {r.signed && r.cents !== 0 ? (r.cents < 0 ? '−' : '+') : ''}
            {formatMoney(Math.abs(r.cents), 'USD')}
          </span>
        </div>
      ))}
      <div className="bg-muted/40 flex items-center justify-between px-4 py-3">
        <span className="font-semibold">Total owed</span>
        <span className="text-lg font-bold tabular-nums text-emerald-600">
          {formatMoney(data.totalOwedCents, 'USD')}
        </span>
      </div>
    </div>
  );
}

/** Cohort collection state: invoiced vs collected, pending markup. */
function CollectionOverview({ data }: { data: Statement }) {
  const lines = (data.byAccount ?? []) as AccountLine[];
  const pct =
    data.cohortInvoicedCents > 0
      ? Math.round((data.cohortPaidCents / data.cohortInvoicedCents) * 100)
      : 0;
  const creditedLaterCents = lines
    .filter((l) => l.settledLater)
    .reduce((sum, l) => sum + (l.settledLater?.markupCents ?? l.markupUnpaidCents), 0);
  const paidPendingCents = lines
    .filter((l) => l.invoiceNowPaid && !l.settledLater)
    .reduce((sum, l) => sum + l.markupUnpaidCents, 0);
  const stillPendingCents = Math.max(
    0,
    data.cohortMarkupUnpaidCents - creditedLaterCents - paidPendingCents,
  );

  return (
    <div className="rounded-lg border p-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <p className="text-muted-foreground text-xs">Invoiced to merchants</p>
          <p className="text-lg font-semibold tabular-nums">
            {formatMoney(data.cohortInvoicedCents, 'USD')}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Collected</p>
          <p className="text-lg font-semibold tabular-nums text-emerald-600">
            {formatMoney(data.cohortPaidCents, 'USD')}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Unpaid at freeze</p>
          <p className="text-lg font-semibold tabular-nums text-amber-600">
            {formatMoney(data.cohortUnpaidCents, 'USD')}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-muted-foreground mb-1 flex justify-between text-xs">
          <span>Collection at freeze time</span>
          <span>{pct}%</span>
        </div>
        <div className="bg-muted h-2 overflow-hidden rounded-full">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {creditedLaterCents > 0 && (
        <p className="mt-3 text-xs text-emerald-700 dark:text-emerald-400">
          {formatMoney(creditedLaterCents, 'USD')} of markup unpaid at freeze was later credited on
          a subsequent statement (see per-merchant links).
        </p>
      )}
      {paidPendingCents > 0 && (
        <p className="text-muted-foreground mt-2 text-xs">
          {formatMoney(paidPendingCents, 'USD')} of markup is on invoices paid after freeze — it
          will be credited on the next weekly statement.
        </p>
      )}
      {stillPendingCents > 0 && (
        <p className="text-muted-foreground mt-2 text-xs">
          {formatMoney(stillPendingCents, 'USD')} of markup is still pending on unpaid invoices; it
          will be credited once collected.
        </p>
      )}
    </div>
  );
}

const hasActivity = (a: AccountLine) =>
  a.invoicedCents !== 0 || a.paidCents !== 0 || a.markupPaidCents !== 0 || a.markupUnpaidCents !== 0;

/** Per-merchant table: search, sorted by invoiced, zero rows collapsed. */
function MerchantTable({ lines, partner }: { lines: AccountLine[]; partner: string }) {
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const { visible, inactiveCount } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? lines.filter((a) => (a.accountName || a.accountId).toLowerCase().includes(q))
      : lines;
    const sorted = [...matches].sort((a, b) => b.invoicedCents - a.invoicedCents);
    const active = sorted.filter(hasActivity);
    const inactive = sorted.filter((a) => !hasActivity(a));
    return {
      visible: showInactive || q ? sorted : active,
      inactiveCount: q ? 0 : inactive.length,
    };
  }, [lines, query, showInactive]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Per-merchant breakdown</h3>
        <div className="relative">
          <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search merchant…"
            className="h-8 w-56 pl-8 text-sm"
          />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Merchant</TableHead>
              <TableHead className="text-right">Invoiced</TableHead>
              <TableHead className="text-right">Collected</TableHead>
              <TableHead className="text-right">Unpaid at freeze</TableHead>
              <TableHead className="text-right">Markup earned</TableHead>
              <TableHead className="text-right">Markup pending</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-8 text-center text-sm">
                  No merchants match.
                </TableCell>
              </TableRow>
            ) : (
              visible.map((a) => {
                const resolved = isResolvedAfterFreeze(a);
                return (
                  <TableRow key={a.billingReportId ?? a.accountId}>
                    <TableCell className="text-sm font-medium">
                      <div className="flex flex-col gap-1">
                        <span className="flex flex-wrap items-baseline gap-2">
                          {a.accountName || a.accountId}
                          {a.invoicePdf && (
                            <a
                              href={a.invoicePdf}
                              target="_blank"
                              rel="noreferrer"
                              title="Invoice PDF"
                              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[11px] font-normal"
                            >
                              <FileText className="h-3.5 w-3.5" />
                              {a.invoiceNumber ?? 'Invoice'}
                            </a>
                          )}
                          {a.invoiceUrl && a.unpaidCents > 0 && !resolved && (
                            <a
                              href={a.invoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                              title="Stripe payment page for this invoice"
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              Pay link
                            </a>
                          )}
                        </span>
                        <CollectionFollowUpNote line={a} partner={partner} />
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(a.invoicedCents, 'USD')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600">
                      {formatMoney(a.paidCents, 'USD')}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        a.unpaidCents > 0 && !resolved
                          ? 'text-amber-600'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {formatMoney(a.unpaidCents, 'USD')}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatMoney(a.markupPaidCents, 'USD')}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${
                        resolved ? 'text-muted-foreground line-through' : 'text-muted-foreground'
                      }`}
                    >
                      {formatMoney(a.markupUnpaidCents, 'USD')}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {inactiveCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground mt-2"
          onClick={() => setShowInactive((v) => !v)}
        >
          {showInactive
            ? 'Hide merchants with no activity'
            : `Show ${inactiveCount} merchants with no activity this week`}
        </Button>
      )}
    </div>
  );
}

/** Late payments / refunds / chargebacks settled on this statement. */
function ReconciliationTable({ adjustments }: { adjustments: Adjustment[] }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold">Reconciliation ({adjustments.length})</h3>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Merchant</TableHead>
              <TableHead>Original week</TableHead>
              <TableHead className="text-right">Markup impact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adjustments.map((adj, i) => (
              <TableRow key={`${adj.billingReportId}-${i}`}>
                <TableCell className="text-sm capitalize">{adj.kind.replace('_', ' ')}</TableCell>
                <TableCell className="text-sm">{adj.accountName || adj.accountId}</TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {adj.originPeriodStart ? fmtDate(adj.originPeriodStart) : '—'}
                </TableCell>
                <TableCell
                  className={`text-right font-medium tabular-nums ${
                    adj.markupCents < 0 ? 'text-red-600' : 'text-emerald-600'
                  }`}
                >
                  {adj.markupCents < 0 ? '−' : '+'}
                  {formatMoney(Math.abs(adj.markupCents), 'USD')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function PartnerStatementDetailPage() {
  const { partner: partnerParam, reportId } = useParams<{ partner: string; reportId: string }>();
  const { partner, canView } = usePartnerAdmin(partnerParam);

  const { data, isLoading, error } = trpc.partnerReports.get.useQuery(
    { partner: partner ?? undefined, reportId: reportId ?? '' },
    { enabled: canView && !!partner && !!reportId, staleTime: 5 * 60 * 1000 },
  );

  if (!canView) return null;

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-destructive font-medium">Failed to load statement</p>
        <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
        {partner && (
          <Button variant="outline" size="sm" className="mt-4" asChild>
            <Link to={PARTNER_ADMIN_ROUTES(partner).REPORTS}>Back to statements</Link>
          </Button>
        )}
      </div>
    );
  }

  if (isLoading || !data || !partner) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StatementHeader partner={partner} data={data} />

      <div className="grid gap-4 lg:grid-cols-2">
        <OwedBreakdown data={data} />
        <CollectionOverview data={data} />
      </div>

      <MerchantTable partner={partner} lines={(data.byAccount ?? []) as AccountLine[]} />

      {(data.adjustments ?? []).length > 0 && (
        <ReconciliationTable adjustments={data.adjustments ?? []} />
      )}
    </div>
  );
}
